import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Mail, MessageSquare, AlertCircle } from 'lucide-react';
import { searchAPI, templatesAPI, campaignsAPI, settingsAPI, api } from '../services/api';
import Modal from './Modal';
import { useCredits } from '../contexts/CreditsContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import SetupErrorMessage from './SetupErrorMessage';
import './AutoPilotWizard.css';

const GOOGLE_DOMAINS = [
  { value: 'google.co.uk', label: 'United Kingdom' },
  { value: 'google.com',   label: 'United States' },
  { value: 'google.com.au', label: 'Australia' },
  { value: 'google.ca',    label: 'Canada' },
  { value: 'google.co.nz', label: 'New Zealand' },
  { value: 'google.ie',    label: 'Ireland' },
  { value: 'google.de',    label: 'Germany' },
  { value: 'google.fr',    label: 'France' },
  { value: 'google.es',    label: 'Spain' },
  { value: 'google.it',    label: 'Italy' },
  { value: 'google.nl',    label: 'Netherlands' },
  { value: 'google.co.za', label: 'South Africa' },
];

const AutoPilotWizard = ({ onClose, onSuccess }) => {
  const navigate = useNavigate();
  const { credits, refreshCredits } = useCredits();
  const { refresh: refreshOnboarding } = useOnboarding();
  const [step, setStep] = useState(1); // 1=questions, 2=processing, 3=review
  const [loadingState, setLoadingState] = useState(''); // 'searching', 'generating', 'campaign'
  const [error, setError] = useState('');
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);

  // Initialisation state
  const [loadingInit, setLoadingInit] = useState(true);
  const [availableProviders, setAvailableProviders] = useState({ email: [], sms: [] });
  const [resendDomains, setResendDomains] = useState([]);
  const [settingsInfo, setSettingsInfo] = useState({});

  // Form data
  const [formData, setFormData] = useState({
    industry: '',
    location: '',
    googleDomain: '', // no default country: the user chooses
    goal: '',
    channel: 'email',
    emailProvider: '',
    fromEmail: '',
    fromName: '',
    resendDomain: '',
    schedule: 'immediate',
    scheduledDate: ''
  });

  // Results data
  const [searchResults, setSearchResults] = useState(null);
  const [generatedTemplate, setGeneratedTemplate] = useState(null);
  const [templateId, setTemplateId] = useState(null);
  const [campaignId, setCampaignId] = useState(null);
  const [campaignCreated, setCampaignCreated] = useState(false);
  const [launching, setLaunching] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoadingInit(true);
      const data = await settingsAPI.getAll();
      const settings = data.settings || {};
      setSettingsInfo(settings);

      // Determine which providers are configured
      const emailProviders = [];
      if (settings.RESEND_API_KEY) emailProviders.push('resend');
      if (settings.SENDGRID_API_KEY) emailProviders.push('sendgrid');
      if (settings.SMTP_SERVER && settings.SMTP_USERNAME) emailProviders.push('smtp');

      const smsProviders = [];
      if (settings.TWILIO_SID && settings.TWILIO_AUTH && settings.TWILIO_NUMBER) smsProviders.push('twilio');

      setAvailableProviders({ email: emailProviders, sms: smsProviders });

      const defaultProvider = emailProviders[0] || '';
      const senderEmail = settings.SENDER_EMAIL || '';
      const senderName = settings.SENDER_NAME || '';

      if (defaultProvider === 'resend') {
        // Fetch domains before setting formData so we can pre-fill from_email
        let domains = [];
        try {
          const resp = await api.get('/domains/from-resend');
          domains = (resp.data.domains || []).filter(d => d.verification_status === 'verified');
          setResendDomains(domains);
        } catch (err) {
          console.error('Error fetching Resend domains:', err);
        }
        const firstDomain = domains[0]?.domain || '';
        const localPart = senderEmail.includes('@') ? senderEmail.split('@')[0] : 'hello';
        setFormData(prev => ({
          ...prev,
          emailProvider: defaultProvider,
          fromEmail: firstDomain ? `${localPart}@${firstDomain}` : senderEmail,
          fromName: senderName,
          resendDomain: firstDomain,
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          emailProvider: defaultProvider,
          fromEmail: senderEmail,
          fromName: senderName,
        }));
      }
    } catch (err) {
      console.error('Error loading initial data:', err);
    } finally {
      setLoadingInit(false);
    }
  };

  const fetchResendDomains = async () => {
    try {
      const resp = await api.get('/domains/from-resend');
      const verified = (resp.data.domains || []).filter(d => d.verification_status === 'verified');
      setResendDomains(verified);
      if (verified.length > 0) {
        const firstDomain = verified[0].domain;
        const localPart = formData.fromEmail.includes('@') ? formData.fromEmail.split('@')[0] : 'hello';
        setFormData(prev => ({
          ...prev,
          resendDomain: firstDomain,
          fromEmail: `${localPart}@${firstDomain}`,
        }));
      }
    } catch (err) {
      console.error('Error fetching Resend domains:', err);
    }
  };

  const handleProviderChange = (provider) => {
    if (provider === 'resend') {
      if (resendDomains.length === 0) {
        setFormData(prev => ({ ...prev, emailProvider: provider }));
        fetchResendDomains();
      } else {
        const localPart = formData.fromEmail.includes('@') ? formData.fromEmail.split('@')[0] : 'hello';
        const domain = formData.resendDomain || resendDomains[0]?.domain || '';
        setFormData(prev => ({
          ...prev,
          emailProvider: provider,
          fromEmail: domain ? `${localPart}@${domain}` : prev.fromEmail,
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        emailProvider: provider,
        fromEmail: settingsInfo.SENDER_EMAIL || prev.fromEmail,
      }));
    }
  };

  const handleResendDomainChange = (domain) => {
    const localPart = formData.fromEmail.includes('@') ? formData.fromEmail.split('@')[0] : 'hello';
    setFormData(prev => ({
      ...prev,
      resendDomain: domain,
      fromEmail: `${localPart}@${domain}`,
    }));
  };

  const validateStep1 = () => {
    if (!formData.googleDomain) {
      setError('Choose which country to search in');
      return false;
    }
    if (!formData.location.trim()) {
      setError('Please enter a location');
      return false;
    }
    if (!formData.goal.trim()) {
      setError('Please describe your goal or service');
      return false;
    }
    const emailNeeded = formData.channel !== 'sms';
    if (emailNeeded && !formData.emailProvider) {
      setError('Please select an email provider. Configure one in Settings first.');
      return false;
    }
    if (emailNeeded && !formData.fromEmail.trim()) {
      setError('Please enter a from email address');
      return false;
    }
    if (formData.schedule === 'scheduled' && !formData.scheduledDate) {
      setError('Please select a date and time for scheduled sending');
      return false;
    }
    setError('');
    return true;
  };

  const handleStartAutoPilot = async () => {
    if (!validateStep1()) return;

    // Search size: largest multiple of 20 within min(100, balance), at least 20
    const balanceTotal = credits ? credits.total : null;
    if (balanceTotal !== null && balanceTotal < 20) {
      setError('You need at least 20 credits to run a search. Buy more credits on the Prospects page.');
      return;
    }
    const resultSize = balanceTotal === null ? 100 : Math.max(20, Math.floor(Math.min(100, balanceTotal) / 20) * 20);

    setStep(2);
    setLoadingState('searching');
    setError('');

    try {
      // Step 1: Search for prospects
      const response = await searchAPI.run({
        industry: formData.industry,
        location: formData.location,
        result_size: resultSize,
        google_domain: formData.googleDomain,
      });

      const jobId = response.data.job_id;

      // Poll for search completion
      const pollInterval = setInterval(async () => {
        try {
          const status = await searchAPI.getJobStatus(jobId);

          if (status.data.job.status === 'completed') {
            clearInterval(pollInterval);
            refreshCredits();
            const result = status.data.job.result;

            if (result.buyers_found === 0) {
              setError('No prospects found. Try adjusting your search criteria (broader location, different industry).');
              setStep(1);
            } else {
              setSearchResults({
                buyersFound: result.buyers_found,
                buyerIds: result.added_buyer_ids || [],
                duplicates: result.duplicates_skipped || 0
              });

              // Move to template generation
              handleTemplateGeneration();
            }
          } else if (status.data.job.status === 'failed') {
            clearInterval(pollInterval);
            setError('Search failed: ' + (status.data.job.error || 'Unknown error'));
            setStep(1);
          }
        } catch (err) {
          clearInterval(pollInterval);
          setError('Error checking search status: ' + err.message);
          setStep(1);
        }
      }, 2000);

    } catch (error) {
      console.error('Search initiation error:', error);
      setError('Failed to start search: ' + (error.response?.data?.error || error.message));
      setStep(1);
    }
  };

  const handleTemplateGeneration = async () => {
    setLoadingState('generating');

    try {
      let templateStructure = 'simple';
      let numMessages = 1;

      if (formData.channel === 'both') {
        templateStructure = 'multi';
        numMessages = 2;
      }

      // Generate template with AI
      const response = await templatesAPI.aiGenerateTemplate({
        industry: formData.industry,
        location: formData.location,
        goal: formData.goal,
        template_structure: templateStructure,
        template_type: formData.channel === 'both' ? undefined : formData.channel,
        num_messages: numMessages,
        spacing_mode: 'ai_suggest'
      });

      setGeneratedTemplate(response.data.template);

      const templateName = `Auto Pilot - ${formData.industry} - ${formData.location} - ${new Date().toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}`;

      let savedTemplate;
      if (templateStructure === 'simple') {
        savedTemplate = await templatesAPI.create({
          name: templateName,
          template_type: formData.channel,
          industry: formData.industry,
          subject: response.data.template.subject || '',
          body: response.data.template.body,
          provider: formData.emailProvider || 'smtp',
          from_email: formData.fromEmail || undefined,
          from_name: formData.fromName || undefined,
        });
      } else {
        // Multi-channel: inject provider/from_email into each email message
        const baseConfig = response.data.template.json_config || {};
        const messages = (baseConfig.messages || []).map(msg =>
          msg.type === 'email'
            ? {
                ...msg,
                provider: formData.emailProvider || 'smtp',
                from_email: formData.fromEmail || '',
                from_name: formData.fromName || '',
              }
            : msg
        );
        savedTemplate = await templatesAPI.createJson({
          name: templateName,
          description: `Auto-generated template for ${formData.industry} in ${formData.location}`,
          industry: formData.industry,
          json_config: {
            ...baseConfig,
            messages,
            metadata: {
              ...(baseConfig.metadata || {}),
              emailProvider: formData.emailProvider || 'smtp',
              from_email: formData.fromEmail || '',
              from_name: formData.fromName || '',
            },
          },
        });
      }

      setTemplateId(savedTemplate.data.id);
      refreshOnboarding();

      // Move to campaign creation
      await handleCampaignCreation(savedTemplate.data.id);

    } catch (error) {
      console.error('Template generation error:', error);
      setError('Failed to generate template: ' + (error.response?.data?.error || error.message));
      setStep(1);
    }
  };

  const handleCampaignCreation = async (templateId) => {
    setLoadingState('campaign');

    try {
      const campaignName = `Auto Pilot - ${formData.industry} - ${formData.location} - ${new Date().toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}`;

      const campaignData = {
        name: campaignName,
        description: `Auto-generated campaign: ${formData.goal}`,
        campaign_type: formData.channel === 'email' ? 'email_only' :
                      formData.channel === 'sms' ? 'sms_only' : 'multi_channel',
        steps: [{
          id: 'step_1',
          name: 'Auto Pilot Message',
          channel_type: formData.channel === 'both' ? 'email' : formData.channel,
          template_id: templateId,
          delay_hours: 0,
          delay_minutes: 0,
          is_active: true
        }],
        schedule: {
          trigger_type: formData.schedule === 'immediate' ? 'immediate' : 'scheduled',
          start_date: formData.scheduledDate || null,
          send_times: ['09:00', '14:00'],
          days_of_week: [1, 2, 3, 4, 5],
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          max_sends_per_hour: 50,
          throttle_delay: 5
        },
        targeting: {
          buyer_ids: searchResults.buyerIds,
          industries: [formData.industry],
          locations: [formData.location],
          exclude_dnc: true,
          max_recipients: searchResults.buyersFound
        }
      };

      const campaign = await campaignsAPI.create(campaignData);

      setCampaignId(campaign.data.campaign_id);
      setCampaignCreated(true);

      // Move to review step
      setStep(3);

    } catch (error) {
      console.error('Campaign creation error:', error);
      setError('Failed to create campaign: ' + (error.response?.data?.error || error.message));
      setStep(1);
    }
  };

  const handleLaunch = async () => {
    setLaunching(true);
    setError('');

    try {
      const result = await campaignsAPI.launch(campaignId);
      refreshOnboarding();

      if (onSuccess) {
        onSuccess(`Campaign launched! ${result.data.messages_scheduled || 0} messages scheduled for ${result.data.buyers_count || searchResults.buyersFound} prospects.`);
      }
      onClose();
      navigate('/campaigns');

    } catch (error) {
      console.error('Launch error:', error);
      setError('Failed to launch campaign: ' + (error.response?.data?.error || error.message));
    } finally {
      setLaunching(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="step-indicator">
      <div className={`step-dot ${step === 1 ? 'active' : ''}`}></div>
      <div className={`step-dot ${step === 2 ? 'active' : ''}`}></div>
      <div className={`step-dot ${step === 3 ? 'active' : ''}`}></div>
    </div>
  );

  const renderStep1 = () => {
    const emailNeeded = formData.channel !== 'sms';
    const smsUnavailable = availableProviders.sms.length === 0 && !loadingInit;
    const noEmailProviders = emailNeeded && availableProviders.email.length === 0 && !loadingInit;

    return (
      <div className="autopilot-step">
        <h3>Tell us about your prospects</h3>

        <div className="form-group">
          <label>What industry do you target?</label>
          <input
            type="text"
            value={formData.industry}
            onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
            placeholder="e.g. Plumbing, Roofing, Landscaping"
          />
        </div>

        <div className="form-group">
          <label>Where are they located?</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            placeholder="e.g., London, Manchester, UK"
          />
        </div>

        <div className="form-group">
          <label>Search Region</label>
          <select
            value={formData.googleDomain}
            onChange={(e) => setFormData({ ...formData, googleDomain: e.target.value })}
            required
          >
            <option value="" disabled>Choose a country...</option>
            {GOOGLE_DOMAINS.map(d => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
          <small className="ap-help-text">Finds businesses via the regional Google — match this to your location</small>
        </div>

        <div className="form-group">
          <label>What's your main goal or service?</label>
          <textarea
            value={formData.goal}
            onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
            placeholder="e.g., Generate leads for emergency plumbing services"
            rows={3}
          />
        </div>

        <div className="form-group">
          <label>Channel</label>
          <div className="channel-options">
            <label className={formData.channel === 'email' ? 'selected' : ''}>
              <input
                type="radio"
                value="email"
                checked={formData.channel === 'email'}
                onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
              />
              <Mail size={24} />
              <span>Email Only</span>
            </label>

            <label className={`${formData.channel === 'sms' ? 'selected' : ''}${smsUnavailable ? ' disabled' : ''}`}>
              <input
                type="radio"
                value="sms"
                checked={formData.channel === 'sms'}
                onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
                disabled={smsUnavailable}
              />
              <MessageSquare size={24} />
              <span>SMS Only</span>
              {smsUnavailable && <small>Twilio not configured</small>}
            </label>

            <label className={`${formData.channel === 'both' ? 'selected' : ''}${smsUnavailable ? ' disabled' : ''}`}>
              <input
                type="radio"
                value="both"
                checked={formData.channel === 'both'}
                onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
                disabled={smsUnavailable}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Mail size={20} />
                <span>+</span>
                <MessageSquare size={20} />
              </div>
              <span>Email + SMS</span>
              <small>{smsUnavailable ? 'Twilio not configured' : 'Email first, SMS follow-up'}</small>
            </label>
          </div>
        </div>

        {/* ── Email Provider + From Address ── */}
        {emailNeeded && (
          <>
            {noEmailProviders ? (
              <div className="ap-no-provider">
                <AlertCircle size={16} />
                <span>No email provider configured. <a href="/settings">Go to Settings</a> to add Resend or SMTP.</span>
              </div>
            ) : (
              <div className="form-group">
                <label>Email Provider</label>
                {loadingInit ? (
                  <p className="ap-help-text">Loading configured providers…</p>
                ) : (
                  <div className="provider-options">
                    {availableProviders.email.map(p => (
                      <label
                        key={p}
                        className={`provider-option${formData.emailProvider === p ? ' selected' : ''}`}
                      >
                        <input
                          type="radio"
                          value={p}
                          checked={formData.emailProvider === p}
                          onChange={() => handleProviderChange(p)}
                        />
                        {p === 'resend' ? 'Resend' : p === 'sendgrid' ? 'SendGrid' : 'SMTP'}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {formData.emailProvider === 'resend' && (
              <div className="form-group">
                <label>Sending Domain</label>
                {resendDomains.length === 0 ? (
                  <p className="ap-help-text">
                    No verified Resend domains found.{' '}
                    <a href="/settings">Add and verify a domain in Settings.</a>
                  </p>
                ) : (
                  <select
                    value={formData.resendDomain}
                    onChange={(e) => handleResendDomainChange(e.target.value)}
                  >
                    {resendDomains.map(d => (
                      <option key={d.id || d.domain} value={d.domain}>{d.domain}</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {formData.emailProvider && (
              <>
                <div className="form-group">
                  <label>From Email</label>
                  {formData.emailProvider === 'resend' && formData.resendDomain ? (
                    <div className="from-email-compose">
                      <input
                        type="text"
                        placeholder="hello"
                        value={formData.fromEmail.includes('@') ? formData.fromEmail.split('@')[0] : formData.fromEmail}
                        onChange={(e) =>
                          setFormData({ ...formData, fromEmail: `${e.target.value}@${formData.resendDomain}` })
                        }
                      />
                      <span className="at-domain">@{formData.resendDomain}</span>
                    </div>
                  ) : (
                    <input
                      type="email"
                      value={formData.fromEmail}
                      onChange={(e) => setFormData({ ...formData, fromEmail: e.target.value })}
                      placeholder="your@email.com"
                    />
                  )}
                </div>

                <div className="form-group">
                  <label>
                    Sender Name <span className="optional-label">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.fromName}
                    onChange={(e) => setFormData({ ...formData, fromName: e.target.value })}
                    placeholder="e.g., John from LeadCo"
                  />
                </div>
              </>
            )}
          </>
        )}

        <div className="form-group">
          <label>When to start?</label>
          <div className="schedule-options">
            <label>
              <input
                type="radio"
                value="immediate"
                checked={formData.schedule === 'immediate'}
                onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
              />
              Send Immediately
            </label>

            <label>
              <input
                type="radio"
                value="scheduled"
                checked={formData.schedule === 'scheduled'}
                onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
              />
              Schedule for Later
            </label>
          </div>

          {formData.schedule === 'scheduled' && (
            <input
              type="datetime-local"
              value={formData.scheduledDate}
              min={new Date().toISOString().slice(0, 16)}
              onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
              style={{ marginTop: '12px' }}
            />
          )}
        </div>

        {error && <div className="error-message"><SetupErrorMessage message={error} /></div>}

        <button
          className="btn btn-primary"
          onClick={handleStartAutoPilot}
          disabled={noEmailProviders && emailNeeded}
        >
          Start Auto Pilot →
        </button>
      </div>
    );
  };

  const renderStep2 = () => (
    <div className="autopilot-loading">
      <div className="progress-steps">
        <div className={`progress-step ${loadingState === 'searching' ? 'active' : searchResults ? 'complete' : ''}`}>
          {loadingState === 'searching' && !searchResults && <div className="spinner"></div>}
          {searchResults && <div className="checkmark">✓</div>}
          <div className="progress-text">
            <span>Searching for prospects...</span>
            {searchResults && <span className="count">Found {searchResults.buyersFound} prospects</span>}
          </div>
        </div>

        <div className={`progress-step ${loadingState === 'generating' ? 'active' : generatedTemplate ? 'complete' : ''}`}>
          {loadingState === 'generating' && !generatedTemplate && <div className="spinner"></div>}
          {generatedTemplate && <div className="checkmark">✓</div>}
          <div className="progress-text">
            <span>Creating AI template...</span>
            {generatedTemplate && <span className="count">Template ready</span>}
          </div>
        </div>

        <div className={`progress-step ${loadingState === 'campaign' ? 'active' : campaignCreated ? 'complete' : ''}`}>
          {loadingState === 'campaign' && !campaignCreated && <div className="spinner"></div>}
          {campaignCreated && <div className="checkmark">✓</div>}
          <div className="progress-text">
            <span>Setting up campaign...</span>
            {campaignCreated && <span className="count">Campaign ready</span>}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="autopilot-summary">
      <h3>✓ Auto Pilot Setup Complete!</h3>

      <div className="summary-card">
        <div className="summary-section">
          <h4>Prospects Found</h4>
          <p className="big-number">{searchResults?.buyersFound || 0}</p>
          <p className="sub-text">{formData.industry} in {formData.location}</p>
          {searchResults?.duplicates > 0 && (
            <p className="note">{searchResults.duplicates} duplicates skipped</p>
          )}
        </div>

        <div className="summary-section">
          <h4>Template Created</h4>
          <p className="template-name">{generatedTemplate?.name || 'Auto Pilot Template'}</p>
          <p className="sub-text">
            {formData.channel === 'both' ? 'Email + SMS sequence' : `${formData.channel} message`}
          </p>
          {formData.fromEmail && (
            <p className="sub-text">From: {formData.fromEmail}</p>
          )}
          <button className="btn-text" onClick={() => setShowTemplatePreview(true)}>
            Preview Template
          </button>
        </div>

        <div className="summary-section">
          <h4>Campaign Ready</h4>
          <p className="campaign-name">
            Auto Pilot - {formData.industry} - {formData.location}
          </p>
          <p className="sub-text">
            {formData.schedule === 'immediate'
              ? 'Will send immediately'
              : `Scheduled for ${formData.scheduledDate ? new Date(formData.scheduledDate).toLocaleString() : 'later'}`}
          </p>
        </div>
      </div>

      {error && <div className="error-message"><SetupErrorMessage message={error} /></div>}

      <div className="action-buttons">
        <button
          className="btn btn-secondary"
          onClick={() => navigate(`/campaigns/${campaignId}`)}
        >
          Review & Edit Campaign
        </button>

        <button
          className="btn btn-primary"
          onClick={handleLaunch}
          disabled={launching}
        >
          {launching ? 'Launching...' : '🚀 Launch Campaign Now'}
        </button>
      </div>

      {showTemplatePreview && (
        <Modal
          isOpen={true}
          onClose={() => setShowTemplatePreview(false)}
          title="Template Preview"
          size="large"
        >
          <div className="template-preview">
            {generatedTemplate?.subject && (
              <div style={{ marginBottom: '16px' }}>
                <strong>Subject:</strong> {generatedTemplate.subject}
              </div>
            )}
            <div>
              <strong>Body:</strong>
              <div
                style={{ marginTop: '8px', padding: '12px', background: 'var(--surface-subtle)', borderRadius: '4px' }}
                dangerouslySetInnerHTML={{ __html: generatedTemplate?.body || 'No preview available' }}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );

  return (
    <div className="autopilot-wizard" onClick={onClose}>
      <div className="autopilot-content" onClick={(e) => e.stopPropagation()}>
        <div className="autopilot-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>🚀 Auto Pilot</h2>
            <button className="close-btn" onClick={onClose}>
              <X size={24} />
            </button>
          </div>
          <p style={{ margin: '8px 0 0 0', color: 'var(--text-secondary)' }}>
            Set up a complete campaign in minutes
          </p>
          {renderStepIndicator()}
        </div>

        <div className="autopilot-body">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>
      </div>
    </div>
  );
};

export default AutoPilotWizard;
